import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import AppBreadcrumb from "../../Shared/AppBreadcrumb";
import FecthingSpinner from "../../Shared/FecthingSpinner";
import httpService from "../../../services/http.service";
import MyOpinionLogo from "../../../assets/images/MyOpinion_Logo_Head.png";

const paymentTypeOptions = [
  { value: "PAYIN", label: "PAYIN" },
  { value: "PAYOUT", label: "PAYOUT" },
];

const feeTypeOptions = [
  { value: "FIXED", label: "Fixe" },
  { value: "PERCENTAGE", label: "Pourcentage" },
  { value: "MIXED", label: "Mixte" },
];

const DEFAULT_FEE_CAP = 1000000;
const DEFAULT_MIN_AMOUNT = 200;
const DEFAULT_MAX_AMOUNT = 1000000;

const emptyFeeForm = () => ({
  country_id: null,
  provider_id: null,
  payment_type: "PAYIN",
  fee_type: "PERCENTAGE",
  fee_percentage: 0,
  fee_fixed: 0,
  fee_cap: DEFAULT_FEE_CAP,
  currency_code: "",
  channel_id: null,
  min_amount: null,
  max_amount: null,
});

const emptyChannelForm = () => ({
  channel_name: "",
  channel_tag: "",
  channel_logo: "",
  description: "",
  status: true,
});

/** Affichage sûr pour <input type="number"> — NaN provoque un écran blanc avec React. */
function displayNumInput(v) {
  if (v == null || v === "") return "";
  const n = typeof v === "number" ? v : Number(v);
  if (Number.isNaN(n)) return "";
  return String(n);
}

function parseNumInput(raw) {
  if (raw === "" || raw == null) return null;
  const n = parseFloat(String(raw));
  return Number.isNaN(n) ? null : n;
}

/**
 * Formate un message lisible à partir d'une erreur axios / réponse API Laravel.
 * Gère : message métier, validation 422, 404, 5xx, erreur réseau.
 */
function formatApiError(error, options = {}) {
  const { skipGenericValidationMessage = false } = options;

  if (!error?.response) {
    if (error?.message === "Network Error") {
      return "Connexion impossible. Vérifiez votre réseau.";
    }
    return error?.message || "Une erreur est survenue.";
  }

  const { status, data } = error.response;
  const errs = data?.errors;
  const hasValidationErrors =
    errs && typeof errs === "object" && !Array.isArray(errs) && Object.keys(errs).length > 0;

  const apiMessage =
    typeof data?.message === "string" && data.message.trim() ? data.message.trim() : "";

  const isGenericLaravelValidation =
    hasValidationErrors &&
    /given data was invalid|données.*invalides/i.test(apiMessage);

  if (apiMessage && !(skipGenericValidationMessage && isGenericLaravelValidation)) {
    return apiMessage;
  }

  if (hasValidationErrors) {
    return Object.entries(errs)
      .map(([key, val]) => {
        const text = Array.isArray(val) ? val.join(", ") : String(val);
        return `${key}: ${text}`;
      })
      .join(" — ");
  }

  if (status === 404) return apiMessage || "Ressource introuvable.";
  if (status === 403) return apiMessage || "Action non autorisée.";
  if (status === 422) return apiMessage || "Données invalides.";
  if (status >= 500) return apiMessage || "Erreur serveur. Réessayez plus tard.";

  return apiMessage || "Une erreur est survenue.";
}

function getApiSuccessMessage(response, fallback = "") {
  const msg = response?.data?.message;
  return typeof msg === "string" && msg.trim() ? msg.trim() : fallback;
}

function showSuccessToast(response, fallback) {
  toast.success(getApiSuccessMessage(response, fallback));
}

function resolveFeeCountryRecord(countryId, navCountry, countries) {
  if (!countryId) return navCountry || null;
  if (navCountry?.id === countryId) return navCountry;
  return (countries || []).find((c) => c.id === countryId) || null;
}

function getFeeCurrencyCode(countryRecord) {
  return String(countryRecord?.currency_code ?? "")
    .trim()
    .toUpperCase();
}

/**
 * Corps JSON aligné sur PaymentMethodFeeController::store|update.
 * min_amount / max_amount : synchronisés côté API via provider_payment_channels.
 */
function buildFeeSubmitBody(feeForm, countryRecord) {
  const currencyCode = getFeeCurrencyCode(countryRecord);
  const hasChannel = feeForm.channel_id != null && feeForm.channel_id !== "";
  const isFixed = feeForm.fee_type === "FIXED";

  const body = {
    country_id: Number(feeForm.country_id),
    provider_id: Number(feeForm.provider_id),
    channel_id: hasChannel ? Number(feeForm.channel_id) : null,
    payment_type: feeForm.payment_type || "PAYIN",
    fee_type: feeForm.fee_type,
    fee_percentage: Number(feeForm.fee_percentage ?? 0),
    fee_fixed: Number(feeForm.fee_fixed ?? 0),
    fee_cap: isFixed ? null : Number(feeForm.fee_cap ?? DEFAULT_FEE_CAP),
    currency_code: currencyCode,
  };

  if (hasChannel) {
    body.min_amount = Number(feeForm.min_amount ?? DEFAULT_MIN_AMOUNT);
    body.max_amount = Number(feeForm.max_amount ?? DEFAULT_MAX_AMOUNT);
  }

  return body;
}

/** Mappe les erreurs Laravel (errors.country_id, …) vers le formulaire. */
function mapApiErrorsToFeeFields(apiErrors) {
  if (!apiErrors || typeof apiErrors !== "object" || Array.isArray(apiErrors)) {
    return {};
  }
  const fieldMap = {
    "data.country_id": "country_id",
    "data.provider_id": "provider_id",
    "data.channel_id": "channel_id",
    "data.currency_code": "currency_code",
    "data.fee_percentage": "fee_percentage",
    "data.fee_fixed": "fee_fixed",
    "data.fee_cap": "fee_cap",
    "data.min_amount": "min_amount",
    "data.max_amount": "max_amount",
  };
  const mapped = {};
  Object.entries(apiErrors).forEach(([key, val]) => {
    const field = fieldMap[key] || key;
    const text = Array.isArray(val) ? val[0] : String(val);
    if (text) mapped[field] = text;
  });
  return mapped;
}

/**
 * Validation des champs obligatoires et cohérence des montants.
 * @returns {{ valid: boolean, errors: Record<string, string>, message: string }}
 */
function validateFeeFormFields(feeForm, countryRecord) {
  const errors = {};

  if (!feeForm.country_id) {
    errors.country_id = "Le pays est obligatoire.";
  }

  const currencyCode = getFeeCurrencyCode(countryRecord);
  if (feeForm.country_id && currencyCode.length !== 3) {
    errors.currency_code = "La devise du pays est introuvable ou invalide.";
  }

  if (!feeForm.provider_id) {
    errors.provider_id = "Le provider est obligatoire.";
  }

  const ft = feeForm.fee_type;
  const pct = Number(feeForm.fee_percentage ?? 0);
  const fixed = Number(feeForm.fee_fixed ?? 0);

  if (ft === "PERCENTAGE") {
    if (feeForm.fee_percentage == null || pct <= 0) {
      errors.fee_percentage = "Indiquez un pourcentage supérieur à 0.";
    }
  } else if (ft === "FIXED") {
    if (feeForm.fee_fixed == null || fixed <= 0) {
      errors.fee_fixed = "Indiquez un montant fixe supérieur à 0.";
    }
  } else if (ft === "MIXED") {
    if (feeForm.fee_percentage == null || pct <= 0) {
      errors.fee_percentage = "Le pourcentage doit être supérieur à 0.";
    }
    if (feeForm.fee_fixed == null || fixed <= 0) {
      errors.fee_fixed = "Le montant fixe doit être supérieur à 0.";
    }
  }

  if (ft === "PERCENTAGE" || ft === "MIXED") {
    const cap = feeForm.fee_cap ?? DEFAULT_FEE_CAP;
    if (cap == null || Number(cap) <= 0) {
      errors.fee_cap = "Le plafond doit être supérieur à 0.";
    }
  }

  const hasChannel =
    feeForm.channel_id != null && feeForm.channel_id !== "";
  if (hasChannel) {
    const min = feeForm.min_amount ?? DEFAULT_MIN_AMOUNT;
    const max = feeForm.max_amount ?? DEFAULT_MAX_AMOUNT;
    if (min == null || Number(min) < 0) {
      errors.min_amount = "Le montant minimum est obligatoire (≥ 0).";
    }
    if (max == null || Number(max) <= 0) {
      errors.max_amount = "Le montant maximum est obligatoire (> 0).";
    }
    if (
      !errors.min_amount &&
      !errors.max_amount &&
      min != null &&
      max != null &&
      Number(min) > Number(max)
    ) {
      errors.min_amount =
        "Le montant minimum ne peut pas dépasser le maximum.";
      errors.max_amount =
        "Le montant maximum doit être supérieur ou égal au minimum.";
    }
  }

  const errorKeys = Object.keys(errors);
  return {
    valid: errorKeys.length === 0,
    errors,
    message:
      errorKeys.length === 0
        ? ""
        : "Veuillez corriger les champs signalés ci-dessous.",
  };
}

function feeFieldControlClass(hasError, extraClasses = "") {
  return `form-control border${extraClasses ? ` ${extraClasses}` : ""}${
    hasError ? " is-invalid" : ""
  }`;
}

function feeTypeShowsPercentage(feeType) {
  return feeType === "PERCENTAGE" || feeType === "MIXED";
}

function feeTypeShowsFixed(feeType) {
  return feeType === "FIXED" || feeType === "MIXED";
}

/** Liste des canaux proposés dans le modal (actifs + canal déjà lié au frais). */
function getFeeModalChannelList(channels, feeOptionsChannels, linkedChannelId) {
  const active = channels.filter((c) => c.status !== false);
  const fromOptions = feeOptionsChannels || [];
  const base = fromOptions.length ? fromOptions : active;
  if (linkedChannelId && !base.some((c) => c.id === linkedChannelId)) {
    const extra = channels.find((c) => c.id === linkedChannelId);
    return extra ? [...base, extra] : base;
  }
  return base;
}

/**
 * Canal déjà identifiable.
 * - Édition : uniquement si le frais a déjà un channel_id.
 * - Création : si un seul canal actif sur la méthode (auto-sélection).
 * @returns {{ channelId: number|null, channel: object|null, locked: boolean }}
 */
function resolveKnownChannel(channelList, editingFee, { isCreate = false } = {}) {
  if (editingFee?.channel_id != null && editingFee.channel_id !== "") {
    const ch =
      editingFee.channel ||
      channelList.find((c) => c.id === editingFee.channel_id);
    return {
      channelId: editingFee.channel_id,
      channel: ch || null,
      locked: true,
    };
  }
  if (isCreate) {
    const selectable = channelList.filter((c) => c.status !== false);
    if (selectable.length === 1) {
      return {
        channelId: selectable[0].id,
        channel: selectable[0],
        locked: true,
      };
    }
  }
  return { channelId: null, channel: null, locked: false };
}

function formatChannelDisplay(ch) {
  if (!ch) return "";
  const name = ch.channel_name || ch.channel_tag || `#${ch.id}`;
  return ch.channel_tag && ch.channel_name
    ? `${ch.channel_name} (${ch.channel_tag})`
    : name;
}

const PaymentMethodDetails = ({ match }) => {
  const methodId = match.params.methodId ?? match.params.id;
  const countryIdForNav =
    match.params.methodId != null && match.params.methodId !== ""
      ? match.params.id
      : null;
  const backToMethodsPath = countryIdForNav
    ? `/payment-countries/${countryIdForNav}/methods`
    : "/payment-methods";

  const [method, setMethod] = useState(null);
  const [country, setCountry] = useState(null);
  const [feeOptions, setFeeOptions] = useState({
    countries: [],
    providers: [],
    channels: [],
  });
  const [fees, setFees] = useState([]);
  const [fetching, setFetching] = useState(true);
  const [loadError, setLoadError] = useState("");

  const [feeModalOpen, setFeeModalOpen] = useState(false);
  const [feeEditingId, setFeeEditingId] = useState(null);
  const [feeForm, setFeeForm] = useState(() => emptyFeeForm());
  const [feeModalSaving, setFeeModalSaving] = useState(false);
  const [feeModalError, setFeeModalError] = useState("");
  const [feeFieldErrors, setFeeFieldErrors] = useState({});

  const clearFeeFieldError = (field) => {
    setFeeFieldErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
    setFeeModalError("");
  };

  const renderFeeFieldError = (field) =>
    feeFieldErrors[field] ? (
      <div className="invalid-feedback d-block">{feeFieldErrors[field]}</div>
    ) : null;

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState("");

  const [channels, setChannels] = useState([]);
  const [channelModalOpen, setChannelModalOpen] = useState(false);
  const [channelEditingId, setChannelEditingId] = useState(null);
  const [channelForm, setChannelForm] = useState(emptyChannelForm());
  const [channelSaving, setChannelSaving] = useState(false);
  const [channelModalError, setChannelModalError] = useState("");

  const mapFeeRow = (f) => ({
    id: f.id,
    country_id: f.country_id,
    provider_id: f.provider_id,
    payment_type: f.payment_type || "PAYIN",
    fee_type: f.fee_type || "PERCENTAGE",
    fee_percentage: f.fee_percentage ?? 0,
    fee_fixed: f.fee_fixed ?? 0,
    fee_cap: f.fee_cap ?? null,
    currency_code: f.currency_code || "",
    channel_id: f.channel_id ?? null,
    status: f.status !== false,
    min_amount: f.min_amount ?? null,
    max_amount: f.max_amount ?? null,
    country: f.country,
    provider: f.provider,
    channel: f.channel,
  });

  const setFeesFromList = (feesRaw, filterCountryId = null) => {
    let list = Array.isArray(feesRaw) ? feesRaw : [];
    if (filterCountryId != null && filterCountryId !== "") {
      const cid = parseInt(filterCountryId, 10);
      list = list.filter((f) => Number(f.country_id) === cid);
    }
    setFees(list.map(mapFeeRow));
  };

  const loadFeesFromMethod = (resData, filterCountryId = null) => {
    const feesRaw = Array.isArray(resData?.fees)
      ? resData.fees
      : resData?.payment_fees || [];
    setFeesFromList(feesRaw, filterCountryId);
  };

  const fetchFeesForMethod = async (filterCountryId = null) => {
    const params =
      filterCountryId != null && filterCountryId !== ""
        ? { country_id: parseInt(filterCountryId, 10) }
        : {};
    const feesRes = await httpService.get(`/payment-methods/${methodId}/fees`, {
      params,
    });
    setFeesFromList(feesRes.data?.data);
  };

  const fetchData = useCallback(async () => {
    setFetching(true);
    setLoadError("");
    try {
      const methodRes = await httpService.get(`/payment-methods/${methodId}`);
      const resData = methodRes.data?.data;
      setMethod(resData || null);

      try {
        await fetchFeesForMethod(countryIdForNav);
      } catch (feesErr) {
        console.warn(
          "Liste frais dédiée indisponible, repli sur la méthode :",
          feesErr?.response?.data || feesErr
        );
        if (resData) loadFeesFromMethod(resData, countryIdForNav);
        else setFees([]);
      }

      if (countryIdForNav) {
        try {
          const countryRes = await httpService.get(`/payment-countries/${countryIdForNav}`);
          setCountry(countryRes.data?.data?.country || countryRes.data?.data || null);
        } catch (_) {
          setCountry(null);
        }
      }

      let channelsList = [];
      try {
        const chRes = await httpService.get(
          `/payment-methods/${methodId}/channels`
        );
        channelsList = Array.isArray(chRes.data?.data) ? chRes.data.data : [];
      } catch (_) {
        channelsList = Array.isArray(resData?.channels) ? resData.channels : [];
      }
      setChannels(channelsList);

      try {
        const optionsRes = await httpService.get(
          `/payment-methods/${methodId}/fees/options`
        );
        const opt = optionsRes.data?.data;
        if (opt) {
          const optChannels = opt.channels || [];
          setFeeOptions({
            countries: opt.countries || [],
            providers: opt.providers || [],
            channels: optChannels.length
              ? optChannels
              : channelsList.filter((c) => c.status !== false),
          });
        }
      } catch (optErr) {
        console.warn(
          "Options frais indisponibles, repli sur les pays de la méthode :",
          optErr?.response?.data || optErr
        );
        const countries = resData?.countries || [];
        setFeeOptions((prev) => ({
          countries: countries.length ? countries : prev.countries,
          providers: prev.providers,
          channels: prev.channels,
        }));
      }
    } catch (error) {
      console.error("Erreur chargement frais:", error?.response?.data || error);
      setMethod(null);
      setChannels([]);
      setFees([]);
      setLoadError(formatApiError(error));
    } finally {
      setFetching(false);
    }
  }, [methodId, countryIdForNav]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (!feeModalOpen || feeEditingId) return;
    const channelList = getFeeModalChannelList(
      channels,
      feeOptions.channels,
      null
    );
    const known = resolveKnownChannel(channelList, null, { isCreate: true });

    setFeeForm((f) => {
      const next = { ...f };
      if (countryIdForNav && !next.country_id) {
        next.country_id = parseInt(countryIdForNav, 10);
      }
      if (known.locked && known.channelId != null && next.channel_id == null) {
        next.channel_id = known.channelId;
      }
      if (next.channel_id != null) {
        if (next.min_amount == null) next.min_amount = DEFAULT_MIN_AMOUNT;
        if (next.max_amount == null) next.max_amount = DEFAULT_MAX_AMOUNT;
      }
      const countryRecord = resolveFeeCountryRecord(
        next.country_id,
        country,
        feeOptions.countries
      );
      if (countryRecord?.currency_code) {
        next.currency_code = countryRecord.currency_code;
      }
      return next;
    });
  }, [
    feeModalOpen,
    feeEditingId,
    country,
    countryIdForNav,
    channels,
    feeOptions.channels,
    feeOptions.countries,
  ]);

  const openFeeModal = (fee = null) => {
    setFeeModalError("");
    setFeeFieldErrors({});
    const channelList = getFeeModalChannelList(
      channels,
      feeOptions.channels,
      fee?.channel_id ?? null
    );
    const knownOnCreate = resolveKnownChannel(channelList, null, {
      isCreate: true,
    });

    if (fee) {
      setFeeForm({
        country_id: fee.country_id,
        provider_id: fee.provider_id,
        payment_type: fee.payment_type || "PAYIN",
        fee_type: fee.fee_type || "PERCENTAGE",
        fee_percentage: fee.fee_percentage ?? 0,
        fee_fixed: fee.fee_fixed ?? 0,
        fee_cap: fee.fee_cap ?? DEFAULT_FEE_CAP,
        currency_code:
          fee.country?.currency_code ||
          resolveFeeCountryRecord(
            fee.country_id,
            country,
            feeOptions.countries
          )?.currency_code ||
          fee.currency_code ||
          "",
        channel_id: fee.channel_id ?? null,
        min_amount: fee.channel_id
          ? fee.min_amount ?? DEFAULT_MIN_AMOUNT
          : null,
        max_amount: fee.channel_id
          ? fee.max_amount ?? DEFAULT_MAX_AMOUNT
          : null,
      });
      setFeeEditingId(fee.id);
    } else {
      setFeeForm({
        ...emptyFeeForm(),
        country_id: countryIdForNav ? parseInt(countryIdForNav, 10) : null,
        currency_code: country?.currency_code || "",
        channel_id: knownOnCreate.locked ? knownOnCreate.channelId : null,
        min_amount: knownOnCreate.locked ? DEFAULT_MIN_AMOUNT : null,
        max_amount: knownOnCreate.locked ? DEFAULT_MAX_AMOUNT : null,
      });
      setFeeEditingId(null);
    }
    setFeeModalOpen(true);
  };

  const feeCountryRecord = resolveFeeCountryRecord(
    feeForm.country_id,
    country,
    feeOptions.countries
  );
  const feeCurrencyCode = getFeeCurrencyCode(feeCountryRecord);

  const submitFeeModal = async () => {
    const validation = validateFeeFormFields(feeForm, feeCountryRecord);
    if (!validation.valid) {
      setFeeFieldErrors(validation.errors);
      setFeeModalError(validation.message);
      return;
    }
    setFeeFieldErrors({});
    setFeeModalError("");

    const body = buildFeeSubmitBody(feeForm, feeCountryRecord);

    setFeeModalSaving(true);
    try {
      let response;
      if (feeEditingId) {
        response = await httpService.put(
          `/payment-methods/${methodId}/fees/${feeEditingId}`,
          body
        );
      } else {
        response = await httpService.post(`/payment-methods/${methodId}/fees`, body);
      }
      showSuccessToast(
        response,
        feeEditingId ? "Frais mis à jour." : "Frais créé."
      );
      setFeeModalOpen(false);
      setFeeFieldErrors({});
      setFeeModalError("");
      await fetchData();
    } catch (error) {
      const apiFieldErrors = mapApiErrorsToFeeFields(
        error?.response?.data?.errors
      );
      if (Object.keys(apiFieldErrors).length > 0) {
        setFeeFieldErrors((prev) => ({ ...prev, ...apiFieldErrors }));
      }
      setFeeModalError(
        formatApiError(error, {
          skipGenericValidationMessage: Object.keys(apiFieldErrors).length > 0,
        })
      );
    } finally {
      setFeeModalSaving(false);
    }
  };

  const feeModalChannels = getFeeModalChannelList(
    channels,
    feeOptions.channels,
    feeForm.channel_id
  );

  const knownChannel = feeEditingId
    ? resolveKnownChannel(feeModalChannels, {
        channel_id: feeForm.channel_id,
        channel: feeModalChannels.find((c) => c.id === feeForm.channel_id),
      })
    : resolveKnownChannel(feeModalChannels, null, { isCreate: true });

  const feeChannelLocked =
    knownChannel.locked && knownChannel.channelId != null;

  const feeChannelDisplay =
    formatChannelDisplay(knownChannel.channel) ||
    formatChannelDisplay(
      feeModalChannels.find((c) => c.id === feeForm.channel_id)
    );

  const openDeleteModal = (fee) => {
    setDeleteError("");
    setDeleteTarget(fee);
  };

  const confirmDelete = async () => {
    if (!deleteTarget?.id) return;
    setDeleteLoading(true);
    setDeleteError("");
    try {
      const response = await httpService.delete(
        `/payment-methods/${methodId}/fees/${deleteTarget.id}`
      );
      showSuccessToast(response, "Frais supprimé.");
      setDeleteTarget(null);
      setDeleteError("");
      await fetchData();
    } catch (error) {
      console.error(error?.response?.data || error);
      setDeleteError(formatApiError(error));
    } finally {
      setDeleteLoading(false);
    }
  };

  const openChannelModal = (ch = null) => {
    setChannelModalError("");
    if (ch) {
      setChannelForm({
        channel_name: ch.channel_name || "",
        channel_tag: ch.channel_tag || "",
        channel_logo: ch.channel_logo || "",
        description: ch.description || "",
        status: ch.status !== false,
      });
      setChannelEditingId(ch.id);
    } else {
      setChannelForm(emptyChannelForm());
      setChannelEditingId(null);
    }
    setChannelModalOpen(true);
  };

  const submitChannelModal = async () => {
    setChannelModalError("");
    if (!channelForm.channel_name.trim() || !channelForm.channel_tag.trim()) {
      setChannelModalError("Nom et tag du canal sont obligatoires.");
      return;
    }
    const body = {
      channel_name: channelForm.channel_name.trim(),
      channel_tag: channelForm.channel_tag.trim(),
      channel_logo: channelForm.channel_logo.trim() || null,
      description: channelForm.description.trim() || null,
      status: channelForm.status !== false,
    };
    setChannelSaving(true);
    try {
      let response;
      if (channelEditingId) {
        response = await httpService.put(
          `/payment-methods/${methodId}/channels/${channelEditingId}`,
          body
        );
      } else {
        response = await httpService.post(
          `/payment-methods/${methodId}/channels`,
          body
        );
      }
      showSuccessToast(
        response,
        channelEditingId ? "Canal mis à jour." : "Canal créé."
      );
      setChannelModalOpen(false);
      await fetchData();
    } catch (error) {
      setChannelModalError(formatApiError(error));
    } finally {
      setChannelSaving(false);
    }
  };

  const countryLabel = (fee) =>
    fee.country?.name_fr ||
    fee.country?.name_en ||
    feeOptions.countries.find((c) => c.id === fee.country_id)?.name_fr ||
    fee.country_id;

  const providerLabel = (fee) =>
    fee.provider?.provider_name ||
    feeOptions.providers.find((p) => p.id === fee.provider_id)?.provider_name ||
    fee.provider_id;

  const channelLabel = (fee) =>
    fee.channel?.channel_name ||
    (fee.channel_id
      ? feeOptions.channels.find((ch) => ch.id === fee.channel_id)?.channel_name
      : "—") ||
    "—";

  if (fetching) {
    return (
      <div className="container-fluid">
        <AppBreadcrumb title="Frais — méthode de paiement" />
        <FecthingSpinner />
      </div>
    );
  }

  if (!method) {
    return (
      <div className="container-fluid">
        <AppBreadcrumb title="Frais — méthode de paiement" />
        <div className="alert alert-danger mt-2 rounded shadow-sm border-0">
          {loadError || "Méthode de paiement introuvable."}
          <Link to={backToMethodsPath} className="alert-link ml-2">
            Retour à la liste
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid payment-method-fees-page">
      <AppBreadcrumb title="Frais — méthode de paiement" />

      <div
        className="card border-0 shadow-sm mb-4 overflow-hidden"
        style={{ borderRadius: "12px" }}
      >
        <div
          className="px-4 py-3 d-flex flex-wrap align-items-center justify-content-between"
          style={{
            background: "linear-gradient(135deg, #1e3a5f 0%, #2d5a87 100%)",
            color: "#fff",
          }}
        >
          <div className="d-flex align-items-center">
            <img
              src={method?.method_logo || MyOpinionLogo}
              alt=""
              className="rounded-circle mr-3 border border-light"
              width={56}
              height={56}
              style={{ objectFit: "cover" }}
            />
            <div>
              <h5 className="mb-0 font-weight-semibold">
                {method?.method_name}
              </h5>
              {country && (
                <div className="d-flex align-items-center mt-1" style={{ opacity: 0.85 }}>
                  {country.flag && (
                    <img
                      src={country.flag}
                      alt=""
                      className="mr-1 rounded"
                      style={{ width: 20, height: 14, objectFit: "cover" }}
                    />
                  )}
                  <span className="small">
                    {country.name_fr || country.name_en || country.code}
                    {country.code && (
                      <span className="ml-1 text-white-50">({country.code})</span>
                    )}
                  </span>
                </div>
              )}
            </div>
          </div>
          <div className="d-flex flex-wrap mt-2 mt-md-0" style={{ gap: "8px" }}>
            <button
              type="button"
              className="btn btn-light btn-sm font-weight-medium px-3"
              style={{ borderRadius: "8px" }}
              onClick={() => openFeeModal()}
            >
              + Ajouter un frais
            </button>
          </div>
        </div>
      </div>

      <div
        className="card border-0 shadow-sm mb-4"
        style={{ borderRadius: "12px" }}
      >
        <div className="card-body p-0">
          <div className="d-flex align-items-center justify-content-between px-4 py-3 border-bottom bg-white">
            <h6 className="mb-0 text-secondary">Canaux de la méthode</h6>
            {country && (
                <div className="d-flex align-items-center mt-1" style={{ opacity: 0.85 }}>
                  {country.flag && (
                    <img
                      src={country.flag}
                      alt=""
                      className="mr-1 rounded"
                      style={{ width: 20, height: 14, objectFit: "cover" }}
                    />
                  )}
                  <span className="small text-black-50">
                    {country.name_fr || country.name_en || country.code}
                    {country.code && (
                      <span className="ml-1">({country.code})</span>
                    )}
                  </span>
                </div>
              )}
          </div>
          <div className="table-responsive">
            <table className="table table-hover mb-0" style={{ fontSize: "0.92rem" }}>
              <thead>
                <tr
                  style={{
                    background: "#f4f6f9",
                    color: "#495057",
                    borderBottom: "2px solid #dee2e6",
                  }}
                >
                  <th className="border-0 pl-4 py-3">Nom</th>
                  <th className="border-0 py-3">Tag</th>
                  <th className="border-0 py-3">Description</th>
                  <th className="border-0 py-3">Actif</th>
                  <th
                    className="border-0 pr-4 py-3 text-right"
                    style={{ width: 56 }}
                  >
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {channels.map((ch) => (
                  <tr key={ch.id}>
                    <td className="pl-4 align-middle font-weight-medium">
                      {ch.channel_name}
                    </td>
                    <td className="align-middle">
                      <code className="small">{ch.channel_tag}</code>
                    </td>
                    <td className="align-middle text-muted small">
                      {ch.description || "—"}
                    </td>
                    <td className="align-middle">
                      <span
                        className={`badge ${
                          ch.status ? "badge-success" : "badge-secondary"
                        }`}
                      >
                        {ch.status ? "Oui" : "Non"}
                      </span>
                    </td>
                    <td className="pr-4 align-middle text-right">
                      <div
                        className="d-inline-flex justify-content-end"
                        style={{ gap: "6px" }}
                      >
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-primary d-inline-flex align-items-center justify-content-center p-0"
                          style={{
                            width: "34px",
                            height: "34px",
                            borderRadius: "8px",
                          }}
                          title="Modifier"
                          aria-label="Modifier ce canal"
                          onClick={() => openChannelModal(ch)}
                        >
                          <i className="icon-pencil" aria-hidden />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {channels.length === 0 && (
                  <tr>
                    <td colSpan={5} className="text-center text-muted py-4">
                      Aucun canal. Ajoutez-en un pour les associer aux frais.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div
        className="card border-0 shadow-sm"
        style={{ borderRadius: "12px" }}
      >
        <div className="card-body p-0">
          <div className="d-flex align-items-center justify-content-between px-4 py-2 border-bottom bg-light">
            <h6 className="mb-0 text-secondary small font-weight-bold text-uppercase">
              Frais
            </h6>
            {country && (
                <div className="d-flex align-items-center mt-1" style={{ opacity: 0.85 }}>
                  {country.flag && (
                    <img
                      src={country.flag}
                      alt=""
                      className="mr-1 rounded"
                      style={{ width: 20, height: 14, objectFit: "cover" }}
                    />
                  )}
                  <span className="small text-black-50">
                    {country.name_fr || country.name_en || country.code}
                    {country.code && (
                      <span className="ml-1">({country.code})</span>
                    )}
                  </span>
                </div>
              )}
          </div>
          <div className="table-responsive">
            <table className="table table-hover mb-0" style={{ fontSize: "0.92rem" }}>
              <thead>
                <tr
                  style={{
                    background: "#f4f6f9",
                    color: "#495057",
                    borderBottom: "2px solid #dee2e6",
                  }}
                >
                  <th className="border-0 py-3">Provider</th>
                  <th className="border-0 py-3">Canal</th>
                  <th className="border-0 py-3">Type</th>
                  <th className="border-0 py-3">Frais</th>
                  <th className="border-0 py-3">%</th>
                  <th className="border-0 py-3">Fixe</th>
                  <th className="border-0 py-3">Plafond</th>
                  <th className="border-0 py-3">Devise</th>
                  <th className="border-0 py-3">Min / Max</th>
                  <th className="border-0 py-3">Actif</th>
                  <th
                    className="border-0 pr-4 py-3 text-right"
                    style={{ width: 96 }}
                  >
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {fees.map((fee) => (
                  <tr key={fee.id}>
                    <td className="align-middle">{providerLabel(fee)}</td>
                    <td className="align-middle text-muted">{channelLabel(fee)}</td>
                    <td className="align-middle">
                      <span className={`badge ${fee.payment_type === "PAYIN" ? "badge-success" : "badge-danger"}`}>
                        {fee.payment_type}
                      </span>
                    </td>
                    <td className="align-middle">
                      <span className={`badge badge-primary`}>{fee.fee_type}</span>
                    </td>
                    <td className="align-middle">{fee.fee_percentage ?? "—"}</td>
                    <td className="align-middle">{fee.fee_fixed ?? "—"}</td>
                    <td className="align-middle">{fee.fee_cap ?? "—"}</td>
                    <td className="align-middle font-weight-medium">
                      {fee.currency_code || "—"}
                    </td>
                    <td className="align-middle text-muted small">
                      {fee.min_amount ?? "—"} / {fee.max_amount ?? "—"}
                    </td>
                    <td className="align-middle">
                      <span
                        className={`badge ${
                          fee.status ? "badge-success" : "badge-secondary"
                        }`}
                      >
                        {fee.status ? "Oui" : "Non"}
                      </span>
                    </td>
                    <td className="pr-4 align-middle text-right">
                      <div
                        className="d-inline-flex align-items-center justify-content-end"
                        style={{ gap: "6px" }}
                      >
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-primary d-inline-flex align-items-center justify-content-center p-0"
                          style={{
                            width: "34px",
                            height: "34px",
                            borderRadius: "8px",
                          }}
                          title="Modifier"
                          aria-label="Modifier ce frais"
                          onClick={() => openFeeModal(fee)}
                        >
                          <i className="icon-pencil" aria-hidden />
                        </button>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-danger d-inline-flex align-items-center justify-content-center p-0"
                          style={{
                            width: "34px",
                            height: "34px",
                            borderRadius: "8px",
                          }}
                          title="Supprimer"
                          aria-label="Supprimer ce frais"
                          onClick={() => openDeleteModal(fee)}
                        >
                          <i className="icon-trash" aria-hidden />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {fees.length === 0 && (
                  <tr>
                    <td colSpan={12} className="text-center text-muted py-5">
                      Aucun frais. Cliquez sur « Nouveau frais » pour en ajouter un.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Modal canal */}
      {channelModalOpen && (
        <div
          className="modal fade show d-block"
          tabIndex="-1"
          role="dialog"
          style={{ background: "rgba(15, 23, 42, 0.45)" }}
        >
          <div className="modal-dialog modal-dialog-centered modal-lg" role="document">
            <div
              className="modal-content border-0 shadow-lg"
              style={{ borderRadius: "14px", overflow: "hidden" }}
            >
              <div
                className="modal-header border-0 text-white"
                style={{
                  background: "linear-gradient(135deg, #1e3a5f 0%, #2d5a87 100%)",
                }}
              >
                <h5 className="modal-title mb-0">
                  {channelEditingId ? "Modifier le canal" : "Nouveau canal"}
                </h5>
                <button
                  type="button"
                  className="close text-white"
                  onClick={() => !channelSaving && setChannelModalOpen(false)}
                  disabled={channelSaving}
                >
                  <span aria-hidden="true">&times;</span>
                </button>
              </div>
              <div className="modal-body" style={{ background: "#fafbfc" }}>
                {channelModalError && (
                  <div className="alert alert-danger border-0 shadow-sm mb-3">
                    {channelModalError}
                  </div>
                )}
                <div className="form-group">
                  <label className="small text-secondary">Nom du canal</label>
                  <input
                    type="text"
                    className="form-control border"
                    style={{ borderRadius: "8px" }}
                    value={channelForm.channel_name}
                    onChange={(e) => {
                      const val = e.target.value;
                      setChannelForm((f) => ({ ...f, channel_name: val }));
                    }}
                    disabled={channelSaving}
                  />
                </div>
                <div className="form-group">
                  <label className="small text-secondary">Tag technique (unique)</label>
                  <input
                    type="text"
                    className="form-control border text-uppercase"
                    style={{ borderRadius: "8px" }}
                    placeholder="MTN_MOMO"
                    value={channelForm.channel_tag}
                    onChange={(e) => {
                      const val = e.target.value;
                      setChannelForm((f) => ({ ...f, channel_tag: val }));
                    }}
                    disabled={channelSaving}
                  />
                </div>
                <div className="form-group">
                  <label className="small text-secondary">Logo (chemin, optionnel)</label>
                  <input
                    type="text"
                    className="form-control border"
                    style={{ borderRadius: "8px" }}
                    value={channelForm.channel_logo}
                    onChange={(e) => {
                      const val = e.target.value;
                      setChannelForm((f) => ({ ...f, channel_logo: val }));
                    }}
                    disabled={channelSaving}
                  />
                </div>
                <div className="form-group">
                  <label className="small text-secondary">Description</label>
                  <textarea
                    className="form-control border"
                    style={{ borderRadius: "8px" }}
                    rows={2}
                    value={channelForm.description}
                    onChange={(e) => {
                      const val = e.target.value;
                      setChannelForm((f) => ({ ...f, description: val }));
                    }}
                    disabled={channelSaving}
                  />
                </div>
                <div className="form-group mb-0">
                  <label className="d-flex align-items-center mb-0">
                    <input
                      type="checkbox"
                      checked={channelForm.status}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        setChannelForm((f) => ({ ...f, status: checked }));
                      }}
                      disabled={channelSaving}
                    />
                    <span className="ml-2 text-secondary">Canal actif</span>
                  </label>
                </div>
              </div>
              <div className="modal-footer border-0 bg-white">
                <button
                  type="button"
                  className="btn btn-light border"
                  style={{ borderRadius: "8px" }}
                  onClick={() => setChannelModalOpen(false)}
                  disabled={channelSaving}
                >
                  Annuler
                </button>
                <button
                  type="button"
                  className="btn text-white border-0"
                  style={{
                    borderRadius: "8px",
                    background:
                      "linear-gradient(135deg, #1e3a5f 0%, #2d5a87 100%)",
                  }}
                  onClick={submitChannelModal}
                  disabled={channelSaving}
                >
                  {channelSaving ? "Enregistrement…" : "Enregistrer"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal frais */}
      {feeModalOpen && (
        <>
          <div
            className="modal fade show d-block"
            tabIndex="-1"
            role="dialog"
            style={{ background: "rgba(15, 23, 42, 0.45)" }}
          >
            <div className="modal-dialog modal-lg modal-dialog-centered" role="document">
              <div
                className="modal-content border-0 shadow-lg"
                style={{ borderRadius: "14px", overflow: "hidden" }}
              >
                <div
                  className="modal-header border-0 text-white"
                  style={{
                    background: "linear-gradient(135deg, #1e3a5f 0%, #2d5a87 100%)",
                  }}
                >
                  <h5 className="modal-title mb-0">
                    {feeEditingId ? "Modifier le frais" : "Nouveau frais"}
                  </h5>
                  <button
                    type="button"
                    className="close text-white"
                    onClick={() => !feeModalSaving && setFeeModalOpen(false)}
                    disabled={feeModalSaving}
                  >
                    <span aria-hidden="true">&times;</span>
                  </button>
                </div>
                <div
                  className="modal-body pt-4"
                  style={{ maxHeight: "72vh", overflowY: "auto", background: "#fafbfc" }}
                >
                  {feeModalError && (
                    <div className="alert alert-danger border-0 shadow-sm mb-3">
                      <div>{feeModalError}</div>
                      {Object.keys(feeFieldErrors).length > 0 && (
                        <ul className="mb-0 mt-2 pl-3 small">
                          {Object.entries(feeFieldErrors).map(([key, msg]) => (
                            <li key={key}>{msg}</li>
                          ))}
                        </ul>
                      )}
                    </div>
                  )}
                  <div className="row">
                    <div className="col-md-4 form-group">
                      <label className="font-weight-medium text-secondary small">
                        Canal
                        {!feeChannelLocked && (
                          <span className="text-muted font-weight-normal">
                            {" "}
                            (optionnel)
                          </span>
                        )}
                      </label>
                      {feeChannelLocked ? (
                        <input
                          type="text"
                          className="form-control border bg-light"
                          style={{ borderRadius: "8px" }}
                          readOnly
                          value={feeChannelDisplay}
                        />
                      ) : (
                        <select
                          className={feeFieldControlClass(
                            Boolean(feeFieldErrors.channel_id)
                          )}
                          style={{ borderRadius: "8px" }}
                          value={feeForm.channel_id ?? ""}
                          onChange={(e) => {
                            const val = e.target.value;
                            clearFeeFieldError("channel_id");
                            clearFeeFieldError("min_amount");
                            clearFeeFieldError("max_amount");
                            setFeeForm((f) => ({
                              ...f,
                              channel_id: val ? parseInt(val, 10) : null,
                              ...(val
                                ? {
                                    min_amount:
                                      f.min_amount ?? DEFAULT_MIN_AMOUNT,
                                    max_amount:
                                      f.max_amount ?? DEFAULT_MAX_AMOUNT,
                                  }
                                : { min_amount: null, max_amount: null }),
                            }));
                          }}
                          disabled={feeModalSaving}
                        >
                          <option value="">— Tous canaux —</option>
                          {feeModalChannels.map((ch) => (
                            <option key={ch.id} value={ch.id}>
                              {formatChannelDisplay(ch)}
                            </option>
                          ))}
                        </select>
                      )}
                      {renderFeeFieldError("channel_id")}
                    </div>
                    <div className="col-md-4 form-group">
                      <label className="font-weight-medium text-secondary small">
                        Pays <span className="text-danger">*</span>
                      </label>
                      {countryIdForNav && country ? (
                        <input
                          type="text"
                          className="form-control border bg-light"
                          style={{ borderRadius: "8px" }}
                          readOnly
                          value={`${country.name_fr || country.name_en || country.code}${
                            country.code ? ` (${country.code})` : ""
                          }`}
                        />
                      ) : (
                        <select
                          className={feeFieldControlClass(
                            Boolean(feeFieldErrors.country_id)
                          )}
                          style={{ borderRadius: "8px" }}
                          value={feeForm.country_id || ""}
                          onChange={(e) => {
                            const val = e.target.value;
                            clearFeeFieldError("country_id");
                            clearFeeFieldError("currency_code");
                            const countryId = val ? parseInt(val, 10) : null;
                            const selected = (feeOptions.countries || []).find(
                              (c) => c.id === countryId
                            );
                            setFeeForm((f) => ({
                              ...f,
                              country_id: countryId,
                              currency_code: selected?.currency_code || "",
                            }));
                          }}
                          disabled={feeModalSaving}
                        >
                          <option value="">— Pays —</option>
                          {(feeOptions.countries || []).map((c) => (
                            <option key={c.id} value={c.id}>
                              {c.name_fr || c.name_en || c.code}
                            </option>
                          ))}
                        </select>
                      )}
                      {renderFeeFieldError("country_id")}
                    </div>
                    <div className="col-md-4 form-group">
                      <label className="font-weight-medium text-secondary small">
                        Devise <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        className={feeFieldControlClass(
                          Boolean(feeFieldErrors.currency_code),
                          "bg-light text-uppercase"
                        )}
                        style={{ borderRadius: "8px" }}
                        readOnly
                        placeholder="—"
                        value={feeCurrencyCode}
                        disabled={feeModalSaving}
                      />
                      {renderFeeFieldError("currency_code")}
                    </div>
                    <div className="col-md-6 form-group">
                      <label className="font-weight-medium text-secondary small">
                        Provider <span className="text-danger">*</span>
                      </label>
                      <select
                        className={feeFieldControlClass(
                          Boolean(feeFieldErrors.provider_id)
                        )}
                        style={{ borderRadius: "8px" }}
                        value={feeForm.provider_id || ""}
                        onChange={(e) => {
                          const val = e.target.value;
                          clearFeeFieldError("provider_id");
                          setFeeForm((f) => ({
                            ...f,
                            provider_id: val ? parseInt(val, 10) : null,
                          }));
                        }}
                        disabled={feeModalSaving}
                      >
                        <option value="">— Provider —</option>
                        {(feeOptions.providers || []).map((p) => (
                          <option key={p.id} value={p.id}>
                            {p.provider_name}
                          </option>
                        ))}
                      </select>
                      {renderFeeFieldError("provider_id")}
                    </div>
                    <div className="col-md-6 form-group">
                      <label className="font-weight-medium text-secondary small">
                        Type paiement
                      </label>
                      <select
                        className="form-control border"
                        style={{ borderRadius: "8px" }}
                        value={feeForm.payment_type ?? "PAYIN"}
                        onChange={(e) => {
                          const val = e.target.value;
                          setFeeForm((f) => ({ ...f, payment_type: val }));
                        }}
                        disabled={feeModalSaving}
                      >
                        {paymentTypeOptions.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-md-6 form-group">
                      <label className="font-weight-medium text-secondary small">
                        Type de frais
                      </label>
                      <select
                        className="form-control border"
                        style={{ borderRadius: "8px" }}
                        value={feeForm.fee_type ?? "PERCENTAGE"}
                        onChange={(e) => {
                          const val = e.target.value;
                          clearFeeFieldError("fee_type");
                          clearFeeFieldError("fee_percentage");
                          clearFeeFieldError("fee_fixed");
                          clearFeeFieldError("fee_cap");
                          setFeeForm((f) => ({
                            ...f,
                            fee_type: val,
                            ...(val === "FIXED"
                              ? { fee_cap: null }
                              : {
                                  fee_cap:
                                    f.fee_cap ?? DEFAULT_FEE_CAP,
                                }),
                          }));
                        }}
                        disabled={feeModalSaving}
                      >
                        {feeTypeOptions.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    {feeTypeShowsPercentage(feeForm.fee_type) && (
                      <div className="col-md-6 form-group">
                        <label className="font-weight-medium text-secondary small">
                          Pourcentage (%) <span className="text-danger">*</span>
                        </label>
                        <input
                          type="number"
                          className={feeFieldControlClass(
                            Boolean(feeFieldErrors.fee_percentage)
                          )}
                          style={{ borderRadius: "8px" }}
                          min="0"
                          step="0.01"
                          value={displayNumInput(feeForm.fee_percentage)}
                          onChange={(e) => {
                            const val = e.target.value;
                            clearFeeFieldError("fee_percentage");
                            setFeeForm((f) => ({
                              ...f,
                              fee_percentage: parseNumInput(val),
                            }));
                          }}
                          disabled={feeModalSaving}
                        />
                        {renderFeeFieldError("fee_percentage")}
                      </div>
                    )}
                    {feeTypeShowsFixed(feeForm.fee_type) && (
                      <div className="col-md-6 form-group">
                        <label className="font-weight-medium text-secondary small">
                          Montant fixe <span className="text-danger">*</span>
                        </label>
                        <input
                          type="number"
                          className={feeFieldControlClass(
                            Boolean(feeFieldErrors.fee_fixed)
                          )}
                          style={{ borderRadius: "8px" }}
                          min="0"
                          step="0.01"
                          value={displayNumInput(feeForm.fee_fixed)}
                          onChange={(e) => {
                            const val = e.target.value;
                            clearFeeFieldError("fee_fixed");
                            setFeeForm((f) => ({
                              ...f,
                              fee_fixed: parseNumInput(val),
                            }));
                          }}
                          disabled={feeModalSaving}
                        />
                        {renderFeeFieldError("fee_fixed")}
                      </div>
                    )}
                    {feeForm.channel_id != null && feeForm.channel_id !== "" && (
                      <>
                        <div className="col-md-6 form-group">
                          <label className="font-weight-medium text-secondary small">
                            Montant min (canal){" "}
                            <span className="text-danger">*</span>
                          </label>
                          <input
                            type="number"
                            className={feeFieldControlClass(
                              Boolean(feeFieldErrors.min_amount)
                            )}
                            style={{ borderRadius: "8px" }}
                            min="0"
                            step="0.01"
                            value={displayNumInput(
                              feeForm.min_amount ?? DEFAULT_MIN_AMOUNT
                            )}
                            onChange={(e) => {
                              const val = e.target.value;
                              clearFeeFieldError("min_amount");
                              clearFeeFieldError("max_amount");
                              setFeeForm((f) => ({
                                ...f,
                                min_amount: parseNumInput(val),
                              }));
                            }}
                            disabled={feeModalSaving}
                          />
                          {renderFeeFieldError("min_amount")}
                        </div>
                        <div className="col-md-6 form-group">
                          <label className="font-weight-medium text-secondary small">
                            Montant max (canal){" "}
                            <span className="text-danger">*</span>
                          </label>
                          <input
                            type="number"
                            className={feeFieldControlClass(
                              Boolean(feeFieldErrors.max_amount)
                            )}
                            style={{ borderRadius: "8px" }}
                            min="0"
                            step="0.01"
                            value={displayNumInput(
                              feeForm.max_amount ?? DEFAULT_MAX_AMOUNT
                            )}
                            onChange={(e) => {
                              const val = e.target.value;
                              clearFeeFieldError("max_amount");
                              clearFeeFieldError("min_amount");
                              setFeeForm((f) => ({
                                ...f,
                                max_amount: parseNumInput(val),
                              }));
                            }}
                            disabled={feeModalSaving}
                          />
                          {renderFeeFieldError("max_amount")}
                        </div>
                      </>
                    )}
                    {(feeForm.fee_type === "PERCENTAGE" ||
                      feeForm.fee_type === "MIXED") && (
                      <div className="col-md-6 form-group">
                        <label className="font-weight-medium text-secondary small">
                          Plafond <span className="text-danger">*</span>
                        </label>
                        <input
                          type="number"
                          className={feeFieldControlClass(
                            Boolean(feeFieldErrors.fee_cap)
                          )}
                          style={{ borderRadius: "8px" }}
                          min="0"
                          step="1"
                          value={displayNumInput(
                            feeForm.fee_cap ?? DEFAULT_FEE_CAP
                          )}
                          onChange={(e) => {
                            const val = e.target.value;
                            clearFeeFieldError("fee_cap");
                            setFeeForm((f) => ({
                              ...f,
                              fee_cap: parseNumInput(val),
                            }));
                          }}
                          disabled={feeModalSaving}
                        />
                        {renderFeeFieldError("fee_cap")}
                      </div>
                    )}
                  </div>
                </div>
                <div className="modal-footer border-0 bg-white">
                  <button
                    type="button"
                    className="btn btn-light border"
                    style={{ borderRadius: "8px" }}
                    onClick={() => setFeeModalOpen(false)}
                    disabled={feeModalSaving}
                  >
                    Annuler
                  </button>
                  <button
                    type="button"
                    className="btn text-white border-0"
                    style={{
                      borderRadius: "8px",
                      background: "linear-gradient(135deg, #1e3a5f 0%, #2d5a87 100%)",
                    }}
                    onClick={submitFeeModal}
                    disabled={feeModalSaving}
                  >
                    {feeModalSaving ? "Enregistrement…" : "Enregistrer"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Modal suppression */}
      {deleteTarget && (
        <>
          <div
            className="modal fade show d-block"
            tabIndex="-1"
            role="dialog"
            style={{ background: "rgba(15, 23, 42, 0.45)" }}
          >
            <div className="modal-dialog modal-dialog-centered" role="document">
              <div
                className="modal-content border-0 shadow-lg"
                style={{ borderRadius: "14px", overflow: "hidden" }}
              >
                <div
                  className="modal-header border-0 text-white"
                  style={{
                    background:
                      "linear-gradient(135deg, #1e3a5f 0%, #2d5a87 100%)",
                  }}
                >
                  <div>
                    <h5 className="modal-title mb-1">Suppression du frais</h5>
                    <p className="mb-0 small font-weight-normal opacity-75">
                      Confirmation requise avant suppression définitive
                    </p>
                  </div>
                  <button
                    type="button"
                    className="close text-white"
                    onClick={() => {
                      if (!deleteLoading) {
                        setDeleteError("");
                        setDeleteTarget(null);
                      }
                    }}
                    disabled={deleteLoading}
                  >
                    <span>&times;</span>
                  </button>
                </div>
                <div className="modal-body" style={{ background: "#fafbfc" }}>
                  {deleteError && (
                    <div className="alert alert-danger border-0 shadow-sm mb-3">
                      {deleteError}
                    </div>
                  )}
                  <p className="text-dark mb-3">
                    Vous êtes sur le point de{" "}
                    <strong>supprimer définitivement</strong> ce frais
                    {method?.method_name ? (
                      <>
                        {" "}
                        pour la méthode <strong>{method.method_name}</strong>
                      </>
                    ) : null}
                    . Cette opération est <strong>irréversible</strong> : les
                    montants et règles associés à cette ligne ne seront plus
                    appliqués.
                  </p>
                  <div
                    className="rounded border bg-white p-3 small text-secondary"
                    style={{ borderRadius: "10px" }}
                  >
                    <p
                      className="text-uppercase text-muted mb-2 font-weight-bold"
                      style={{ letterSpacing: "0.06em", fontSize: "0.7rem" }}
                    >
                      Récapitulatif
                    </p>
                    <ul className="list-unstyled mb-0 pl-0">
                      <li className="mb-1">
                        <span className="text-muted">Pays :</span>{" "}
                        <span className="text-dark">
                          {countryLabel(deleteTarget)}
                        </span>
                      </li>
                      <li className="mb-1">
                        <span className="text-muted">Provider :</span>{" "}
                        <span className="text-dark">
                          {providerLabel(deleteTarget)}
                        </span>
                      </li>
                      <li className="mb-1">
                        <span className="text-muted">Canal :</span>{" "}
                        <span className="text-dark">
                          {channelLabel(deleteTarget)}
                        </span>
                      </li>
                      <li className="mb-1">
                        <span className="text-muted">Flux / type de frais :</span>{" "}
                        <span className="text-dark">
                          {deleteTarget.payment_type} — {deleteTarget.fee_type}
                        </span>
                      </li>
                      <li className="mb-0">
                        <span className="text-muted">Devise :</span>{" "}
                        <span className="text-dark">
                          {deleteTarget.currency_code || "—"}
                        </span>
                      </li>
                    </ul>
                  </div>
                </div>
                <div className="modal-footer border-0 bg-light">
                  <button
                    type="button"
                    className="btn btn-light border"
                    style={{ borderRadius: "8px" }}
                    onClick={() => {
                      setDeleteError("");
                      setDeleteTarget(null);
                    }}
                    disabled={deleteLoading}
                  >
                    Annuler
                  </button>
                  <button
                    type="button"
                    className="btn btn-danger"
                    style={{ borderRadius: "8px" }}
                    onClick={confirmDelete}
                    disabled={deleteLoading}
                  >
                    {deleteLoading ? "Suppression…" : "Supprimer"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default PaymentMethodDetails;
